import { useCallback, useEffect, useState } from "react";
import {
  type BillingPeriod,
  type LinkedCardResponse,
  type SeamlessChargeResponse,
  subscriptionApi
} from "@shared/api/subscription.api";
import { extractErrorDetail } from "@shared/helper_lib/api-error";
import { markPaymentHandled } from "@shared/helper_lib/preview-upgrade-return";

export type SeamlessChargeTarget =
  | { kind: "minutes"; minutes: number }
  | { kind: "subscription"; plan: string; billingPeriod: BillingPeriod };

interface SeamlessFailure {
  message: string;
  reason?: string | null;
  /**
   * Можно ли предлагать оплату другой картой. При «платёж принят, банк
   * подтверждает» — нельзя: деньги уже списаны, и вторая попытка означала бы
   * второе списание.
   */
  canRetry: boolean;
}

/**
 * Сохранённый способ оплаты пользователя.
 *
 * Запрашивается один раз при открытии модалки: от него зависит, показываем мы
 * экран согласия со списанием или сразу уводим на ЮKassa.
 */
interface UseLinkedCardReturn {
  card: LinkedCardResponse | null;
  isLoading: boolean;
  hasCard: boolean;
}

export const useLinkedCard = (enabled: boolean): UseLinkedCardReturn => {
  const [card, setCard] = useState<LinkedCardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    subscriptionApi
      .getLinkedCard()
      .then(result => {
        if (!cancelled) {
          setCard(result);
        }
      })
      .catch(() => {
        // Карту узнать не удалось — работаем как раньше, через редирект
        if (!cancelled) {
          setCard({ hasCard: false });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return (): void => {
      cancelled = true;
    };
  }, [enabled]);

  return { card, isLoading, hasCard: card?.hasCard === true };
};

/**
 * Списание с сохранённой карты по кнопке.
 *
 * Хук сам разбирает три исхода: 3-D Secure уводит на страницу банка (в той же
 * вкладке — иначе человек потеряет результат), отказ кладёт причину в failure,
 * успех возвращается вызывающему, чтобы тот показал свой текст.
 */
interface UseSeamlessChargeReturn {
  charge: (target: SeamlessChargeTarget) => Promise<SeamlessChargeResponse | null>;
  isCharging: boolean;
  failure: SeamlessFailure | null;
  reset: () => void;
}

export const useSeamlessCharge = (): UseSeamlessChargeReturn => {
  const [isCharging, setIsCharging] = useState(false);
  const [failure, setFailure] = useState<SeamlessFailure | null>(null);

  const reset = useCallback(() => setFailure(null), []);

  const charge = useCallback(async (target: SeamlessChargeTarget): Promise<SeamlessChargeResponse | null> => {
    setIsCharging(true);
    setFailure(null);

    try {
      const result =
        target.kind === "minutes"
          ? await subscriptionApi.chargeMinutesWithSavedCard(target.minutes)
          : await subscriptionApi.chargeSubscriptionWithSavedCard(target.plan, target.billingPeriod);

      // Второй рубеж к серверному флагу seamless: вкладки, открытые до
      // деплоя, о нём не знают и без отметки уехали бы на страницу успеха
      markPaymentHandled(result.paymentId);

      if (result.status === "failed") {
        setFailure({
          message: result.failureMessage ?? "Банк отклонил платёж.",
          reason: result.failureReason,
          canRetry: true
        });
        return result;
      }

      if (result.status === "confirmation_required" && result.confirmationUrl) {
        window.location.href = result.confirmationUrl;
        return result;
      }

      // Деньги списаны, но зачисление ещё не подтверждено: подтверждение вне
      // браузера (СБП, пуш банка) или эффект догоняет сверка. Обещать
      // результат нельзя, но и как отказ это показывать неверно.
      if (result.status === "processing" || result.status === "confirmation_required") {
        setFailure({
          message:
            "Платёж принят, банк ещё подтверждает его. Зачисление придёт в течение нескольких минут — проверьте баланс или историю платежей.",
          canRetry: false
        });
        return result;
      }

      return result;
    } catch (error: any) {
      // 429 от троттлера приходит без человеческого текста
      if (error?.response?.status === 429) {
        setFailure({
          message: "Слишком много попыток оплаты подряд. Попробуйте через час или оплатите обычным способом.",
          canRetry: false
        });
        return null;
      }

      setFailure({
        message: extractErrorDetail(error, "Сервис оплаты не ответил. Попробуйте ещё раз или оплатите другой картой."),
        canRetry: true
      });
      return null;
    } finally {
      setIsCharging(false);
    }
  }, []);

  return { charge, isCharging, failure, reset };
};
